import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { API_CLIENT_CONFIG } from '../core/api-client-config';
import { PaginationResult } from '../core/pagination';
import {
  AdminTenderDetails,
  AdminTender,
  TenderLot,
} from '../models';

@Injectable({ providedIn: 'root' })
export class AdminTendersApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(API_CLIENT_CONFIG).apiBaseUrl}/api/admin/BackofficeTenders`;

  getTenders(options?: {
    regions?: string[];
    cities?: string[];
    statuses?: string[];
    publicationDateGte?: Date;
    publicationDateLte?: Date;
    expiryDateGte?: Date;
    expiryDateLte?: Date;
    formStatus?: string;
    orderBy?: string;
    orderDirection?: 'Ascending' | 'Descending';
    skip?: number;
    take?: number;
  }) {
    let params = new HttpParams();

    if (options?.regions && options.regions.length > 0) {
      params = params.append('regions', options.regions.join(','));
    }

    if (options?.cities && options.cities.length > 0) {
      params = params.append('cities', options.cities.join(','));
    }

    if (options?.statuses && options.statuses.length > 0) {
      options.statuses.forEach((status) => {
        params = params.append('statuses', status);
      });
    }

    if (options?.publicationDateGte && options?.publicationDateLte) {
      params = params.append(
        'publicationDate.startDate',
        options.publicationDateGte.toISOString(),
      );
      params = params.append(
        'publicationDate.endDate',
        options.publicationDateLte.toISOString(),
      );
    }

    if (options?.expiryDateGte && options?.expiryDateLte) {
      params = params.append(
        'expiryDate.startDate',
        options.expiryDateGte.toISOString(),
      );
      params = params.append(
        'expiryDate.endDate',
        options.expiryDateLte.toISOString(),
      );
    }

    if (options?.formStatus) {
      params = params.append('formStatus', options.formStatus);
    }

    if (options?.orderBy) {
      params = params.append('orderBy.orderBy', options.orderBy);

      if (options?.orderDirection) {
        params = params.append(
          'orderBy.direction',
          options.orderDirection === 'Ascending' ? 'Asc' : 'Desc',
        );
      }
    }

    if (options?.skip !== null && options?.skip !== undefined) {
      params = params.append('skip', options.skip);
    }

    if (options?.take !== null && options?.take !== undefined) {
      params = params.append('take', options.take);
    }

    return this.http.get<PaginationResult<AdminTender>>(this.baseUrl, { params });
  }

  getTender(id: string) {
    return this.http.get<AdminTenderDetails>(`${this.baseUrl}/${id}`);
  }

  getTendersByAuthorityId(contractingAuthorityId: string) {
    return this.http.get<AdminTender[]>(
      `${this.baseUrl}/contracting-authority/${contractingAuthorityId}`,
    );
  }

  createTender(request: {
    contractingAuthorityId: string;
    title: string;
    tenderLots: TenderLot[];
    attachmentsIds: string[];
    reportAttachmentsIds: string[];
    concessionPeriodYears: number;
    concessionPeriodMaxYears: number;
    expiryDate: Date;
    publicationDate: Date;
    reportId: string | null;
    status: 'Draft' | 'Published';
  }) {
    return this.http.post<AdminTender>(this.baseUrl, request);
  }

  updateTender(request: {
    id: string;
    contractingAuthorityId: string;
    title: string;
    tenderLots: TenderLot[];
    attachmentsIds: string[];
    concessionPeriodYears: number;
    concessionPeriodMaxYears: number;
    expiryDate: Date;
    publicationDate: Date;
    reportId: string | null;
    status: 'Draft' | 'Published';
    isEconomicOfferEvaluationEnabled: boolean;
  }) {
    return this.http.put(`${this.baseUrl}`, request);
  }

  deleteTender(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  getMarkerCoordinates(address: string, city: string) {
    return this.http.get<{ latitude: number; longitude: number }>(
      `${this.baseUrl}/geocode`,
      {
        params: {
          address,
          city,
        },
      },
    );
  }

  extractRequirements(id: string) {
    return this.http.post(`${this.baseUrl}/${id}/extract-requirements`, {});
  }
}
